import React, { useState } from 'react';
import { Button, Tabs, Statistic } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { Grid, Image, Form } from 'semantic-ui-react';

import footerLogo from 'images/footerLogo.svg';
import {
  useUserIdByPhoneAndSignId,
  useCheckCertificationSMS,
  useUserIdByEmailAndSignId,
  useCheckCertificationEmail,
  useUpdatePassword
} from './hooks';
import { FormContentWrapper, TabsWrapper, CertificationInputWrapper, SecurityWrapper } from './styles';

const { Countdown } = Statistic;

// 영문, 숫자, 특수문자를 모두 포함하는 8-20자 패스워드 검증 정규식
const PASSWORD_VALIDATION_REGEX = /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+~`\-={}[\]:;"'<>,.?/\\]).{8,20}$/;

const PasswordSecurityIndicator = ({ value }) => {
  const isPasswordSecure = PASSWORD_VALIDATION_REGEX.test(value);
  const securityMessage = isPasswordSecure ? '보안수준 안전' : '보안수준 위험';
  return (
    <SecurityWrapper $isPasswordSecure={isPasswordSecure} $isPassword={!!value}>
      {value ? securityMessage : null}
      <LockOutlined />
    </SecurityWrapper>
  );
};

const FindPasswordForm = ({ onFindPassword }) => {
  const [tabKey, setTabkey] = useState('sms');
  const [formData, setFormData] = useState({});
  const [countdown, setCountdown] = useState(0);

  const [userId, setUserId] = useState(null);
  const [isCertification, setIsCertification] = useState(false);

  const onSuccess = (uid, expireSecond) => {
    const second = expireSecond || 180;
    setUserId(uid);
    setCountdown(Date.now() + 1000 * second);
  };

  // 휴대폰 번호로 찾기
  const { mutateAsync: getUserIdByPhoneAndSignId, isLoading: isUserIdBySMS } = useUserIdByPhoneAndSignId(onSuccess);
  const { mutateAsync: checkCertificationSMS, isLoading: isCheckSMS } = useCheckCertificationSMS(() =>
    setIsCertification(true)
  );
  // 이메일로 찾기
  const { mutateAsync: getUserIdByEmailAndSignId, isLoading: isUserIdByEmail } = useUserIdByEmailAndSignId(onSuccess);
  const { mutateAsync: checkCertificationEmail, isLoading: isCheckEmail } = useCheckCertificationEmail(() =>
    setIsCertification(true)
  );
  // 비밀번호 변경
  const { mutateAsync: updatePassword, isLoading: isPasswordLoading } = useUpdatePassword(onFindPassword);

  const handleChange = (event) => {
    setFormData((prevState) => ({ ...prevState, [event.target.name]: event.target.value }));
  };

  const handleTabsChange = (key) => {
    setTabkey(key);
    setUserId(null);
    setFormData({});
    setIsCertification();
  };

  const handleSubmit = async (isRefetch) => {
    const getUserId = tabKey === 'sms' ? getUserIdByPhoneAndSignId : getUserIdByEmailAndSignId;
    const checkCertification = tabKey === 'sms' ? checkCertificationSMS : checkCertificationEmail;

    if (!isCertification) {
      if (!userId || isRefetch === true) {
        await getUserId(formData);
      } else if (formData.code) {
        const key = tabKey === 'sms' ? 'userid' : 'userId';
        await checkCertification({ [key]: userId, code: formData.code });
      }
    } else {
      const { code, password, verificationPassword } = formData;
      updatePassword({
        userId,
        password,
        verificationPassword,
        type: tabKey.toUpperCase(),
        validationValue: code
      });
    }
  };

  const label = tabKey === 'sms' ? '휴대폰 번호' : '이메일';
  const name = tabKey === 'sms' ? 'phone' : 'email';
  const defaultOption = { className: 'form-input', onChange: handleChange };

  const isSMSLoading = isUserIdBySMS && isCheckSMS;
  const isEmailLoading = isUserIdByEmail && isCheckEmail;

  return (
    <Grid className="login-form" verticalAlign="middle">
      <Grid.Column className="login-form-col">
        <Form size="large" onSubmit={handleSubmit}>
          {!isCertification ? (
            <FormContentWrapper>
              <TabsWrapper defaultActiveKey="sms" onChange={handleTabsChange}>
                <Tabs.TabPane tab="휴대폰 번호로 찾기" key="sms" />
                <Tabs.TabPane tab="이메일로 찾기" key="email" />
              </TabsWrapper>
              <div className="content">
                <Form.Input {...defaultOption} label="아이디" name="signId" value={formData.signId || ''} />
                <Form.Input {...defaultOption} label={label} name={name} value={formData[name] || ''} />
                {userId ? (
                  <CertificationInputWrapper>
                    <Form.Input
                      {...defaultOption}
                      label="인증번호"
                      name="code"
                      placeholder="인증번호 6자리"
                      icon={<Countdown className="count-down" format="mm:ss" value={countdown} />}
                    />
                    <Button style={{ height: '48px' }} onClick={() => handleSubmit(true)}>
                      재발송
                    </Button>
                  </CertificationInputWrapper>
                ) : null}
              </div>
            </FormContentWrapper>
          ) : (
            <FormContentWrapper>
              <div className="title">비밀번호 재설정</div>
              <div className="sub-title">변경할 새로운 비밀번호를 입력해주세요</div>
              <div className="content">
                <Form.Input
                  {...defaultOption}
                  label="비밀번호"
                  name="password"
                  type="password"
                  placeholder="영문, 숫자, 특수문자 포함 8~20자"
                  maxLength={20}
                  icon={<PasswordSecurityIndicator value={formData.password} />}
                />
                <Form.Input
                  {...defaultOption}
                  label="비밀번호 확인"
                  name="verificationPassword"
                  type="password"
                  placeholder="새로운 비밀번호 재입력"
                  maxLength={20}
                  icon={<PasswordSecurityIndicator value={formData.verificationPassword} />}
                />
              </div>
            </FormContentWrapper>
          )}
          <Grid className="form-group" style={{ marginBottom: `${userId && !isCertification ? 16 : 52}px` }}>
            <Button className="login-form-button-channel" onClick={onFindPassword} style={{ marginLeft: 'auto' }}>
              로그인하기
            </Button>
          </Grid>

          <Button
            type="primary"
            htmlType="submit"
            style={{ width: '100%' }}
            disabled={!(!userId === !formData.code)}
            loading={isSMSLoading || isEmailLoading || isPasswordLoading}
          >
            {!userId && !isCertification ? '인증요청' : '확인'}
          </Button>
          {!userId ? <p>고객센터 1644-5047</p> : null}
        </Form>
        <div className="login-form-footer">
          <div className="login-form-footer-img">
            <Image src={footerLogo} />
          </div>
        </div>
      </Grid.Column>
    </Grid>
  );
};

export default FindPasswordForm;
